import { useEffect, useState } from 'react';
import { Outlet, useOutletContext } from 'react-router-dom';
import { useQueryState, IconLoader, Filters } from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import {
  getCardsTransactions,
  onFilterCardsTransactionsList,
  onSortCardsTransactionsList,
  clearAccountsStatementState,
} from '../store/slices/accounts-statements';
import { overviewCardsSchema } from '../variables/overview-cards';
import OverviewStatementsCardsTransaction from '../../../components/partial/overview-statements/overview-statements-cards-transaction';
import { CustomerType, StatementsContextType } from '../types';

import cn from '../customers.module.css';

const AccountCards = ({
  customerType,
  page,
}: {
  customerType: CustomerType;
  page: 'accounts' | 'aml';
}) => {
  const { cards } = useOutletContext<StatementsContextType>();

  const [cardId, setCardId] = useState('');

  const dispatch = useAppDispatch();

  const {
    customerAccountsStatements: { cardsTransactions },
  } = useAppSelector((state) => state);

  const { filters, sort, onSortChange, onFilterChange } =
    useQueryState(overviewCardsSchema);

  const onSort = (name: string) => {
    onSortChange(name);

    dispatch(
      onSortCardsTransactionsList({
        name,
        direction: sort.sortDir === 'asc' ? 'desc' : 'asc',
      }),
    );
  };

  const onFilter = (values: Filters) => {
    const body = { ...values };

    onFilterChange(values);

    if (values.date) {
      body.date.value = values.date.value?.map((item) =>
        new Date(item).toISOString(),
      );
    }

    dispatch(onFilterCardsTransactionsList(body));
  };

  useEffect(() => {
    if (!cardId) {
      setCardId(cards[0]);
    }
  }, [cardId]);

  useEffect(() => {
    dispatch(clearAccountsStatementState());
  }, [dispatch]);

  useEffect(() => {
    if (cardId && !cardsTransactions.values) {
      dispatch(
        getCardsTransactions({
          name: sort?.sortProp,
          direction: sort?.sortDir as 'asc' | 'desc',
          filters: {
            ...filters,
          },
          cardId,
          customerType,
          page,
        }),
      );
    }
  }, [dispatch, cardsTransactions.values, cardId, sort, filters]);

  let content;

  if (cardsTransactions.isLoading) {
    content = (
      <div className={cn.overviewStatementsLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <OverviewStatementsCardsTransaction
        onSort={onSort}
        onFilter={onFilter}
        cardsTransactions={cardsTransactions.values}
        filters={filters}
        sort={sort}
        requestInProgress={cardsTransactions.requestInProgress}
      />
    );
  }

  return (
    <>
      {cards?.length > 1 && (
        <div className={cn.overviewStatementsAccountsWrapper}>
          {cards.map((item) => (
            <button
              key={item}
              type="button"
              className={`${cn.overviewStatementsAccountButton} ${
                cardId === item ? cn.overviewStatementsAccountButtonActive : ''
              }`}
              onClick={() => {
                setCardId(item);
                dispatch(
                  getCardsTransactions({
                    cardId: item,
                    customerType,
                    page,
                  }),
                );
              }}
            >
              {item}
            </button>
          ))}
        </div>
      )}
      <div className={cn.overviewStatementsContentTable}>{content}</div>
      <Outlet />
    </>
  );
};

export default AccountCards;
